import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  MapPin,
  Gauge,
  Fuel,
  Settings,
  Heart,
  Share2,
  Shield,
  ChevronLeft,
  ChevronRight,
  Check,
  Calendar,
  Palette,
  Cog,
} from 'lucide-react'
import { Button, Card, CardContent, Badge } from '@/components/ui'
import { formatPrice, formatMileage } from '@/utils/format'
import { getVehicleById } from '@/data/vehicles'
import { useFavoritesStore } from '@/stores/favorites'

export function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  const vehicle = getVehicleById(id!)
  const { isFavorite, toggleFavorite } = useFavoritesStore()
  const isFav = id ? isFavorite(id) : false

  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Vehicle Not Found</h2>
          <p className="mt-2 text-gray-600">The vehicle you're looking for doesn't exist.</p>
          <Link to="/vehicles" className="mt-4 inline-block">
            <Button>Browse Vehicles</Button>
          </Link>
        </div>
      </div>
    )
  }

  const title = `${vehicle.year} ${vehicle.make} ${vehicle.model}`
  const discount = vehicle.msrpCents
    ? Math.round(((vehicle.msrpCents - vehicle.priceCents) / vehicle.msrpCents) * 100)
    : null

  const prevImage = () =>
    setCurrentImageIndex((i) => (i === 0 ? vehicle.photos.length - 1 : i - 1))
  const nextImage = () =>
    setCurrentImageIndex((i) => (i === vehicle.photos.length - 1 ? 0 : i + 1))

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link
          to="/vehicles"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Vehicles
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden">
              <div className="relative">
                <img
                  src={vehicle.photos[currentImageIndex]}
                  alt={`${title} - View ${currentImageIndex + 1}`}
                  className="w-full h-80 sm:h-96 lg:h-[28rem] object-cover"
                />

                {/* Navigation arrows */}
                {vehicle.photos.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors"
                      aria-label="Next image"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}

                {/* Badges */}
                <div className="absolute top-3 left-3 flex gap-2">
                  {vehicle.certified && (
                    <Badge variant="primary">
                      <Shield className="h-3 w-3 mr-1" />
                      Certified
                    </Badge>
                  )}
                  {discount && discount > 0 && (
                    <Badge variant="success">{discount}% Off MSRP</Badge>
                  )}
                </div>

                {/* Image counter */}
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-sm px-2 py-1 rounded">
                  {currentImageIndex + 1} / {vehicle.photos.length}
                </div>
              </div>
            </Card>

            {/* Thumbnails */}
            {vehicle.photos.length > 1 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {vehicle.photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`relative rounded-lg overflow-hidden aspect-video ${
                      index === currentImageIndex
                        ? 'ring-2 ring-primary-500'
                        : 'hover:opacity-80'
                    }`}
                  >
                    <img
                      src={photo}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Quick Stats */}
            <Card>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-3 rounded-lg bg-gray-50">
                    <Gauge className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-500">Mileage</p>
                    <p className="font-semibold">{formatMileage(vehicle.mileage)}</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-gray-50">
                    <Calendar className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-500">Year</p>
                    <p className="font-semibold">{vehicle.year}</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-gray-50">
                    <Fuel className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-500">Fuel</p>
                    <p className="font-semibold capitalize">{vehicle.fuelType}</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-gray-50">
                    <Cog className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-sm text-gray-500">Transmission</p>
                    <p className="font-semibold capitalize">{vehicle.transmission}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Features & Options</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {vehicle.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Specifications */}
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-gray-100 rounded-lg p-2">
                      <Settings className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Engine</p>
                      <p className="font-medium">{vehicle.engineDescription || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-gray-100 rounded-lg p-2">
                      <Cog className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Drivetrain</p>
                      <p className="font-medium uppercase">{vehicle.drivetrain}</p>
                    </div>
                  </div>
                  {vehicle.mpgCity && vehicle.mpgHighway && (
                    <div className="flex items-center gap-3">
                      <div className="bg-gray-100 rounded-lg p-2">
                        <Fuel className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Fuel Economy</p>
                        <p className="font-medium">
                          {vehicle.mpgCity} city / {vehicle.mpgHighway} hwy MPG
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="bg-gray-100 rounded-lg p-2">
                      <Palette className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Exterior Color</p>
                      <p className="font-medium">{vehicle.exteriorColor}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Price and Actions */}
          <div className="space-y-6">
            {/* Price Card */}
            <Card className="sticky top-24">
              <CardContent className="space-y-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                  <p className="text-gray-600">{vehicle.trim}</p>
                </div>

                <div>
                  <div className="text-3xl font-bold text-primary-600">
                    {formatPrice(vehicle.priceCents)}
                  </div>
                  {vehicle.msrpCents && vehicle.msrpCents > vehicle.priceCents && (
                    <p className="text-sm text-gray-500">
                      <span className="line-through">{formatPrice(vehicle.msrpCents)}</span>
                      <span className="ml-2 text-green-600 font-medium">
                        Save {formatPrice(vehicle.msrpCents - vehicle.priceCents)}
                      </span>
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Gauge className="h-4 w-4" />
                    {formatMileage(vehicle.mileage)}
                  </div>
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    Austin, TX
                  </div>
                </div>

                <hr className="border-gray-200" />

                <div className="space-y-3">
                  <Button className="w-full" size="lg">
                    Start Purchase
                  </Button>
                  <Link to={`/financing?vehicleId=${vehicle.id}&price=${vehicle.priceCents / 100}`}>
                    <Button variant="outline" className="w-full">
                      Get Financing Quote
                    </Button>
                  </Link>
                  <div className="flex gap-2">
                    <Button
                      variant={isFav ? 'primary' : 'ghost'}
                      className="flex-1"
                      onClick={() => id && toggleFavorite(id)}
                    >
                      <Heart className={`h-4 w-4 mr-2 ${isFav ? 'fill-current' : ''}`} />
                      {isFav ? 'Saved' : 'Save'}
                    </Button>
                    <Button variant="ghost" className="flex-1">
                      <Share2 className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Details Card */}
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Vehicle Details</h2>
                <dl className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">VIN</dt>
                    <dd className="font-mono text-xs">{vehicle.vin}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Stock #</dt>
                    <dd>BES-{vehicle.id.toUpperCase()}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Exterior</dt>
                    <dd>{vehicle.exteriorColor}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Interior</dt>
                    <dd>{vehicle.interiorColor}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Body Style</dt>
                    <dd className="capitalize">{vehicle.bodyType}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Drivetrain</dt>
                    <dd className="uppercase">{vehicle.drivetrain}</dd>
                  </div>
                </dl>
                {vehicle.carfaxUrl && (
                  <a
                    href={vehicle.carfaxUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 block text-center text-sm text-primary-600 hover:underline"
                  >
                    View CARFAX Report
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Contact Card */}
            <Card className="bg-gray-900 text-white">
              <CardContent>
                <h3 className="font-semibold mb-2">Questions?</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Our team is here to help with any questions about this vehicle.
                </p>
                <Button variant="outline" className="w-full border-white text-white hover:bg-white/10">
                  Contact Us
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
